import { cn } from "@core-ui/utils/cn";
import { useLowlightActions } from "@ext/markdown/elements/codeBlockLowlight/edit/component/CodeBlockComponent";
import lowlight from "@ext/markdown/elements/codeBlockLowlight/edit/logic/Lowlight";
import { type Lang, normalizeLangName } from "@ext/markdown/elements/codeBlockLowlight/edit/logic/LowlightLangs";
import { splitCodeIntoLines } from "@ext/markdown/elements/codeBlockLowlight/print/splitCodeIntoLines";
import StyledCodeBlock from "@ext/markdown/elements/codeBlockLowlight/render/component/StyledCodeBlock";
import { toJsxRuntime } from "hast-util-to-jsx-runtime";
import type { HTMLAttributes } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { CodeBlockCopyButton } from "./CodeBlockCopyButton";

interface CodeBlockProps {
	children: string;
	language?: Lang;
	style?: HTMLAttributes<HTMLPreElement>["style"];
	withoutHighlight?: boolean;
	isPrint?: boolean;
	className?: string;
}

const CodeBlock = (props: CodeBlockProps) => {
	const { language = "", children = "", withoutHighlight, style, isPrint, className } = props;
	const trimVal = children.trim();
	const normalizedLang = normalizeLangName(language);
	const { isRegistered } = useLowlightActions({ language: normalizedLang });
	if (withoutHighlight) {
		return <StyledCodeBlock style={style}>{trimVal}</StyledCodeBlock>;
	}

	const tree =
		isRegistered && normalizedLang && lowlight.registered(normalizedLang)
			? lowlight.highlight(normalizedLang, trimVal)
			: lowlight.highlight("none", trimVal);

	return (
		<StyledCodeBlock
			className={cn(
				className,
				"group/code",
				`[&_.child-wrapper]:py-[1.375em]`,
				`[&_.child-wrapper]:px-[1.625em]`,
				isPrint && "[&_.child-wrapper]:!whitespace-pre-wrap",
			)}
			isPrint={isPrint}
			spellCheck={false}
			style={style}
		>
			{!isPrint && <CodeBlockCopyButton text={trimVal} />}
			{isPrint ? splitCodeIntoLines(tree) : toJsxRuntime(tree, { jsx, jsxs, Fragment })}
		</StyledCodeBlock>
	);
};

export default CodeBlock;
